import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FileExcelO from '@rsuite/icons/legacy/FileExcelO';
import { Button, Cascader, Divider, Dropdown, IconButton, InputNumber, Message, Modal, Panel, Popover, SelectPicker, Stack, toaster, Tooltip, Whisper } from 'rsuite';

import ProviderBadge from '../components/ProviderBadge';
import './TaskActualsPage.css';
import './ActualsPrint.css';

import useAuthenticatedFetch from '../hooks/useAuthenticatedFetch';
import API_CONFIG from '../config/apiConfig';

import {
  useTask,
  useTasks,
  useUpdateTask,
  useTaskHourForecastForMonths,
  useTaskActualsForMonths,
  useClearTaskActualsForMonth,
  useActiveProvider,
  useUsers,
  useCloseDates,
} from '../hooks/useQueries';

import { formatHours } from '../utils/chartUtils';
import { exportSheetsToXlsx } from '../utils/excelExport';

const isIsoDate = (s) => typeof s === 'string' && /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(s);

const tryParseJsonText = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const isProbablyHtml = (text) => /^\s*<!doctype\s+html/i.test(String(text || '')) || /^\s*<html/i.test(String(text || ''));

const parseTimecampTasksJson = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { tasks: [], error: 'Unexpected JSON shape from TimeCamp (expected object keyed by task_id).' };
  }

  const rows = Object.values(payload);
  const tasks = rows
    .map((r) => {
      const taskId = Number(r?.task_id);
      const parentId = Number(r?.parent_id);
      const hasChildren = r?.hasChildren === true;
      return {
        taskId: Number.isFinite(taskId) ? taskId : null,
        parentId: Number.isFinite(parentId) ? parentId : 0,
        name: String(r?.name || '—'),
        hasChildren,
        raw: r,
      };
    })
    .filter((t) => Number.isInteger(t.taskId));

  // If the API doesn't send hasChildren, infer it from parent relationships.
  const inferredHasChildren = new Set();
  for (const t of tasks) {
    const pid = Number(t.parentId);
    if (Number.isInteger(pid) && pid > 0) inferredHasChildren.add(pid);
  }
  for (const t of tasks) {
    if (!t.hasChildren && inferredHasChildren.has(Number(t.taskId))) {
      t.hasChildren = true;
    }
  }

  return { tasks, error: null };
};

const buildTimecampTaskTree = (tasks) => {
  const nodesById = new Map();
  const parentById = new Map();

  for (const t of tasks || []) {
    const id = Number(t?.taskId);
    if (!Number.isInteger(id)) continue;
    nodesById.set(id, {
      label: String(t?.name || '—'),
      value: id,
      task: t,
    });
    parentById.set(id, Number.isInteger(Number(t?.parentId)) ? Number(t.parentId) : 0);
  }

  const roots = [];
  for (const [id, node] of nodesById.entries()) {
    const parentId = parentById.get(id) ?? 0;
    const parentNode = nodesById.get(parentId);
    if (!parentId || !parentNode) {
      roots.push(node);
      continue;
    }
    if (!Array.isArray(parentNode.children)) parentNode.children = [];
    parentNode.children.push(node);
  }

  const sortTree = (nodes) => {
    nodes.sort((a, b) => String(a?.label || '').localeCompare(String(b?.label || '')));
    for (const n of nodes) {
      if (Array.isArray(n.children) && n.children.length > 0) sortTree(n.children);
    }
  };
  sortTree(roots);

  return { roots, nodesById, parentById };
};

const buildTimecampPathLabels = (taskId, nodesById, parentById) => {
  const id = Number(taskId);
  if (!Number.isInteger(id)) return [];

  const labels = [];
  let cur = id;
  let guard = 0;
  while (Number.isInteger(cur) && guard < 50) {
    const node = nodesById.get(cur);
    if (!node) break;
    labels.push(node.label);
    const parent = parentById.get(cur) ?? 0;
    if (!parent) break;
    cur = parent;
    guard += 1;
  }

  return labels.reverse();
};

const normalizePersonName = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const findBestUserMatchByName = (timecampName, users) => {
  const tcNorm = normalizePersonName(timecampName);
  if (!tcNorm) return { userId: null, reason: 'no_name' };

  const arr = Array.isArray(users) ? users : [];
  const exact = arr.filter((u) => normalizePersonName(u?.name) === tcNorm);
  if (exact.length === 1) return { userId: Number(exact[0].id), reason: 'exact' };
  if (exact.length > 1) return { userId: null, reason: 'ambiguous_exact' };

  const contains = arr.filter((u) => {
    const un = normalizePersonName(u?.name);
    if (!un) return false;
    return un.includes(tcNorm) || tcNorm.includes(un);
  });
  if (contains.length === 1) return { userId: Number(contains[0].id), reason: 'contains' };
  if (contains.length > 1) return { userId: null, reason: 'ambiguous_contains' };

  return { userId: null, reason: 'no_match' };
};

const resolveApiBaseUrl = () => {
  const raw = String(API_CONFIG?.BASE_URL || '').trim();
  if (!raw) return '/api';

  // Normalize: callers sometimes configure BASE_URL as "http://localhost:3001" instead of ".../api".
  const withoutTrailing = raw.replace(/\/+$/, '');
  if (/\/api$/i.test(withoutTrailing)) return withoutTrailing;
  return `${withoutTrailing}/api`;
};

const formatMonthLabel = (monthStart) => {
  if (!isIsoDate(monthStart)) return '—';
  const [y, m] = monthStart.split('-').map((v) => Number(v));
  if (!Number.isInteger(y) || !Number.isInteger(m)) return '—';
  const d = new Date(y, m - 1, 1);
  return d.toLocaleString(undefined, { month: 'short', year: '2-digit' });
};

const toMonthStartIso = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const getCurrentYearStartMonthIso = () => {
  const now = new Date();
  return toMonthStartIso(new Date(now.getFullYear(), 0, 1));
};

const addMonthsIso = (monthStart, deltaMonths) => {
  if (!isIsoDate(monthStart)) return null;
  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return null;
  const d = new Date(y, m0 + Number(deltaMonths || 0), 1);
  d.setHours(0, 0, 0, 0);
  return toMonthStartIso(d);
};

const roundToQuarterHour = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / 0.25) * 0.25;
};

const formatQuarterHours = (value) => {
  const r = roundToQuarterHour(value);
  return r.toFixed(2);
};

const formatDeltaHours = (delta) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return '';
  const r = roundToQuarterHour(n);
  if (Math.abs(r) < 0.01) return '0.00';
  const s = formatQuarterHours(r);
  return r > 0 ? `+${s}` : s;
};

const normalizeIsoTimestamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const latestIsoTimestamp = (a, b) => {
  const aa = normalizeIsoTimestamp(a);
  const bb = normalizeIsoTimestamp(b);
  if (aa && bb) return aa >= bb ? aa : bb;
  return aa || bb || null;
};

const formatDaysAgoLabel = (value) => {
  const iso = normalizeIsoTimestamp(value);
  if (!iso) return null;

  const then = new Date(iso);
  const now = new Date();
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = nowDay.getTime() - thenDay.getTime();
  const days = Number.isFinite(diffMs) ? Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000))) : null;
  if (!Number.isInteger(days)) return null;
  if (days === 0) return 'today';

  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const formatRefreshedLabel = (value) => {
  const iso = normalizeIsoTimestamp(value);
  if (!iso) return null;
  const dayLabel = formatDaysAgoLabel(iso);
  if (!dayLabel) return null;
  if (dayLabel === 'today') {
    const t = new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `today at ${t}`;
  }
  return dayLabel;
};

const countWeekdaysInRange = (startInclusive, endExclusive) => {
  if (!(startInclusive instanceof Date) || !(endExclusive instanceof Date)) return 0;

  const start = new Date(startInclusive.getFullYear(), startInclusive.getMonth(), startInclusive.getDate());
  const end = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start.getTime() >= end.getTime()) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor.getTime() < end.getTime()) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const parseMonthDay = (raw) => {
  const s = String(raw ?? '').slice(0, 10);
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return { month: Number(m[1]), day: Number(m[2]) };
};

const countCloseDatesInRange = (startInclusive, endExclusive, rows) => {
  if (!(startInclusive instanceof Date) || !(endExclusive instanceof Date)) return 0;
  const list = Array.isArray(rows) ? rows : [];

  const start = new Date(startInclusive.getFullYear(), startInclusive.getMonth(), startInclusive.getDate());
  const end = new Date(endExclusive.getFullYear(), endExclusive.getMonth(), endExclusive.getDate());
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  if (start.getTime() >= end.getTime()) return 0;

  const years = new Set();
  for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) years.add(y);

  let count = 0;
  for (const r of list) {
    const md = parseMonthDay(r?.closeDate ?? r?.close_date);
    if (!md || !Number.isFinite(md.month) || !Number.isFinite(md.day)) continue;

    for (const y of years) {
      const d = new Date(y, md.month - 1, md.day);
      d.setHours(0, 0, 0, 0);
      if (d.getTime() >= start.getTime() && d.getTime() < end.getTime()) {
        count += 1;
      }
    }
  }

  return count;
};

const shouldWarnProjectedShortfall = ({ monthStart, projected, actual, currentMonthStart, ftePercent, closeDates }) => {
  if (String(monthStart) !== String(currentMonthStart)) return false;

  const proj = Number(projected);
  const act = Number(actual);
  if (!Number.isFinite(proj) || proj <= 0) return false;
  if (!Number.isFinite(act)) return false;

  const minTarget = proj * 0.95;
  if (act >= minTarget) return false;

  const y = Number(String(monthStart).slice(0, 4));
  const m0 = Number(String(monthStart).slice(5, 7)) - 1;
  if (!Number.isInteger(y) || !Number.isInteger(m0)) return false;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  today.setHours(0, 0, 0, 0);

  const monthStartDate = new Date(y, m0, 1);
  monthStartDate.setHours(0, 0, 0, 0);
  const monthEndExclusive = new Date(y, m0 + 1, 1);
  monthEndExclusive.setHours(0, 0, 0, 0);

  const rangeStart = today.getTime() > monthStartDate.getTime() ? today : monthStartDate;
  if (rangeStart.getTime() >= monthEndExclusive.getTime()) return false;

  const weekdaysRemaining = countWeekdaysInRange(rangeStart, monthEndExclusive);
  const closeDaysRemaining = countCloseDatesInRange(rangeStart, monthEndExclusive, closeDates);

  const rawFte = Number(ftePercent);
  const fteScale = Number.isFinite(rawFte) ? Math.max(0, rawFte) / 100 : 1;
  const remainingCapacity = Math.max(0, weekdaysRemaining * 8 - closeDaysRemaining * 8) * fteScale;

  const maxReachable = act + remainingCapacity;
  return maxReachable < minTarget;
};

const DeltaBadge = ({ delta, projected, actual, shortfallWarning }) => {
  const n = Number(delta);
  if (!Number.isFinite(n)) return null;

  const proj = Number(projected);
  const act = Number(actual);

  const isProjZero = !Number.isFinite(proj) || Math.abs(proj) < 0.01;
  const isActZero = !Number.isFinite(act) || Math.abs(act) < 0.01;

  let bg = '#fffbe6';
  let border = '#ffe58f';
  let color = '#ad8b00';
  let showOverageWarning = false;

  if (isProjZero && isActZero) {
    bg = '#f6ffed';
    border = '#b7eb8f';
    color = '#389e0d';
  } else if (isProjZero && !isActZero) {
    bg = '#fff1f0';
    border = '#ffa39e';
    color = '#cf1322';
    showOverageWarning = true;
  } else {
    const pct = ((act - proj) / Math.abs(proj)) * 100;
    const absPct = Math.abs(pct);

    if (absPct <= 5) {
      bg = '#f6ffed';
      border = '#b7eb8f';
      color = '#389e0d';
    } else if (pct > 10) {
      bg = '#fff1f0';
      border = '#ffa39e';
      color = '#cf1322';
      showOverageWarning = true;
    }
  }

  const warningMessage = showOverageWarning
    ? 'Actual hours are > 110% of the projected hours.'
    : shortfallWarning
      ? 'User will likely not be able to meet the projected hours based on available time in the month.'
      : null;
  const warningColor = showOverageWarning ? '#cf1322' : '#fa8c16';
  const badgeContent = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1px 6px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 800,
        lineHeight: '16px',
        whiteSpace: 'nowrap',
      }}
    >
      {formatDeltaHours(n)}
      {warningMessage ? (
        <span
          aria-label="Hours overage warning"
          style={{ color: warningColor, marginLeft: 4, fontSize: 12, fontWeight: 900, lineHeight: 1 }}
        >
          !
        </span>
      ) : null}
    </span>
  );

  if (!warningMessage) return badgeContent;

  return (
    <Whisper speaker={<Tooltip>{warningMessage}</Tooltip>} trigger={['hover', 'focus']} placement="top">
      {badgeContent}
    </Whisper>
  );
};


function TaskPeopleMonthlyLayout({
  taskId,
  task,
  timecampTaskId,
  activeProviderName,
  provider,
  providerLoading,
  autoPullMonthStart,
  taskForecastPath,
  onOpenTaskForecast,
  actualsReturnPath,
}) {
  const WINDOW_SIZE = 6;

  const isTimeCampActive = activeProviderName === 'timecamp';
  const allowManualEdit = activeProviderName === 'manual' || activeProviderName === null;
  const isOdcTask = String(task?.taskType || '').trim().toLowerCase() === 'odc';

  const currentYearStart = useMemo(() => getCurrentYearStartMonthIso(), []);
  const [startMonth, setStartMonth] = useState(() => getCurrentYearStartMonthIso());

  const [printMode, setPrintMode] = useState(false);

  useEffect(() => {
    if (!printMode) return undefined;

    const handleAfterPrint = () => setPrintMode(false);
    window.addEventListener('afterprint', handleAfterPrint);

    const t = setTimeout(() => {
      try {
        window.print();
      } catch {
        setPrintMode(false);
      }
    }, 60);

    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [printMode]);

  const months = useMemo(() => {
    const list = [];
    for (let i = 0; i < WINDOW_SIZE; i += 1) {
      const ms = addMonthsIso(startMonth, i);
      if (ms) list.push(ms);
    }
    return list;
  }, [WINDOW_SIZE, startMonth]);

  const {
    data: taskHourForecast,
    isLoading: taskHourForecastLoading,
    error: taskHourForecastError,
  } = useTaskHourForecastForMonths(Number.isInteger(taskId) ? taskId : null, months);

  const forecastHoursByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(taskHourForecast?.rows) ? taskHourForecast.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      map.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
    }
    return map;
  }, [taskHourForecast?.rows]);

  const {
    data: taskActuals,
    isLoading: taskActualsLoading,
    error: taskActualsError,
    refetch: refetchTaskActuals,
  } = useTaskActualsForMonths(Number.isInteger(taskId) ? taskId : null, months);

  const actualHoursByUserId = useMemo(() => {
    const map = new Map();
    const rows = Array.isArray(taskActuals?.rows) ? taskActuals.rows : [];
    for (const r of rows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      map.set(uid, r?.hours && typeof r.hours === 'object' ? r.hours : {});
    }
    return map;
  }, [taskActuals?.rows]);

  const monthLastModifiedByMonth = useMemo(() => {
    const map = {};
    const raw = taskActuals?.monthLastModifiedByMonth;
    if (!raw || typeof raw !== 'object') return map;

    for (const [monthStartRaw, tsRaw] of Object.entries(raw)) {
      const monthStartText = String(monthStartRaw || '').slice(0, 10);
      const monthStart = isIsoDate(monthStartText) ? `${monthStartText.slice(0, 7)}-01` : null;
      const ts = normalizeIsoTimestamp(tsRaw);
      if (!monthStart || !ts) continue;
      const prev = map[monthStart];
      if (!prev || ts > prev) map[monthStart] = ts;
    }

    return map;
  }, [taskActuals?.monthLastModifiedByMonth]);

  const people = useMemo(() => {
    const byId = new Map();
    const assignedUserIds = new Set();
    const add = (userId, name, role, timecardLastModifiedAt = null) => {
      const id = Number.isInteger(Number(userId)) ? Number(userId) : null;
      const key = id !== null ? `id:${id}` : `name:${String(name || '').trim()}`;
      if (!key || key === 'name:') return;

      const normalizedLastModified = normalizeIsoTimestamp(timecardLastModifiedAt);

      if (!byId.has(key)) {
        byId.set(key, {
          userId: id,
          name: String(name || '—'),
          roles: new Set(role ? [role] : []),
          notAssigned: false,
          timecardLastModifiedAt: normalizedLastModified,
        });
        if (id !== null && role) assignedUserIds.add(id);
        return;
      }
      const existing = byId.get(key);
      if (role) existing.roles.add(role);
      if ((!existing.name || existing.name === '—') && name) existing.name = String(name);
      if (normalizedLastModified) {
        const prev = normalizeIsoTimestamp(existing.timecardLastModifiedAt);
        if (!prev || normalizedLastModified > prev) {
          existing.timecardLastModifiedAt = normalizedLastModified;
        }
      }
      if (id !== null && role) assignedUserIds.add(id);
    };

    const activities = Array.isArray(task?.activities) ? task.activities : [];
    for (const a of activities) {
      if (a?.leadUserId || a?.leadName) add(a?.leadUserId, a?.leadName, 'lead');
      const supporters = Array.isArray(a?.supporters) ? a.supporters : [];
      for (const s of supporters) add(s?.userId, s?.name, 'support');
    }

    // Include anyone with stored actuals rows, even if they're not on the roster.
    // This is required to surface accepted users that are "Not assigned to task".
    const actualRows = Array.isArray(taskActuals?.rows) ? taskActuals.rows : [];
    for (const r of actualRows) {
      const uid = Number(r?.userId);
      if (!Number.isInteger(uid)) continue;
      const name = String(r?.userName || '').trim() || `User ${uid}`;
      add(uid, name, null, r?.timecardLastModifiedAt);
      const key = `id:${uid}`;
      const rec = byId.get(key);
      if (rec && !assignedUserIds.has(uid)) {
        rec.notAssigned = true;
      }
    }

    // If we still have no people, show a single placeholder row.
    const arr = Array.from(byId.values());
    if (arr.length === 0) {
      return [{ userId: null, name: '—', roles: new Set(), notAssigned: false }];
    }
    arr.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')));
    return arr;
  }, [task?.activities, taskActuals?.rows]);

  const colTemplate = useMemo(() => {
    const monthCols = months.map(() => 'minmax(150px, 1fr)').join(' ');
    return `minmax(110px, 1fr) ${monthCols}`.trim();
  }, [months]);

  const canPull = !isOdcTask && Number.isInteger(timecampTaskId) && Number(timecampTaskId) > 0;
  const currentMonthStart = useMemo(() => toMonthStartIso(new Date()), []);
  const isOnCurrentWindow = startMonth === currentYearStart;

  const goPrevMonths = () => {
    const ns = addMonthsIso(startMonth, -WINDOW_SIZE);
    if (ns) setStartMonth(ns);
  };

  const goNextMonths = () => {
    const ns = addMonthsIso(startMonth, WINDOW_SIZE);
    if (ns) setStartMonth(ns);
  };

  const goCurrentMonths = () => {
    setStartMonth(currentYearStart);
  };

  const monthRangeLabel = useMemo(() => {
    if (months.length === 0) return '';
    const first = formatMonthLabel(months[0]);
    const last = formatMonthLabel(months[months.length - 1]);
    return first === last ? first : `${first} – ${last}`;
  }, [months]);

  const handleExportXlsx = useCallback(async () => {
    const safeTaskName = String(task?.task || task?.name || `Task ${taskId}` || 'Task').trim() || 'Task';
    const fileName = `${safeTaskName} - Actuals.xlsx`;

    const columns = [{ header: 'Person', key: 'person', width: 28 }];
    for (const m of months) {
      const label = formatMonthLabel(m);
      columns.push({ header: `${label} Proj`, key: `proj:${m}`, width: 12, numFmt: '0.00' });
      columns.push({ header: `${label} Act`, key: `act:${m}`, width: 12, numFmt: '0.00' });
    }

    const rowsOut = [];
    for (const p of people) {
      const name = String(p?.name || '—');
      const note = p?.notAssigned ? ' (Not assigned to task)' : '';
      const personLabel = `${name}${note}`;

      const uid = Number(p?.userId);
      const hasUser = Number.isInteger(uid);

      const row = { person: personLabel };
      for (const m of months) {
        const proj = hasUser ? Number(forecastHoursByUserId.get(uid)?.[m] ?? 0) : 0;
        const act = hasUser ? Number(actualHoursByUserId.get(uid)?.[m] ?? 0) : 0;
        row[`proj:${m}`] = Number.isFinite(proj) ? proj : 0;
        row[`act:${m}`] = Number.isFinite(act) ? act : 0;
      }
      rowsOut.push(row);
    }

    // Totals row (same as table totals).
    const totalsRow = { person: 'Total' };
    for (const m of months) {
      totalsRow[`proj:${m}`] = Number(taskHourForecast?.totals?.[m] ?? 0);
      totalsRow[`act:${m}`] = Number(taskActuals?.totals?.[m] ?? 0);
    }
    rowsOut.push(totalsRow);

    await exportSheetsToXlsx({
      fileName,
      sheets: [{ name: 'Actuals', columns, rows: rowsOut }],
    });
  }, [actualHoursByUserId, forecastHoursByUserId, months, people, task?.name, task?.task, taskActuals?.totals, taskHourForecast?.totals, taskId]);

  const authenticatedFetch = useAuthenticatedFetch();
  const pullHoursUrlBase = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/pull-hours`;
  }, [taskId]);

  const [pullingMonth, setPullingMonth] = useState(null);
  const [refreshAttemptedAtByMonth, setRefreshAttemptedAtByMonth] = useState({});

  const [unmatchedModalOpen, setUnmatchedModalOpen] = useState(false);
  const [unmatchedModalMonth, setUnmatchedModalMonth] = useState(null);
  const [unmatchedModalRows, setUnmatchedModalRows] = useState([]);
  const [unmatchedModalEntries, setUnmatchedModalEntries] = useState([]);
  const [unmatchedRemediationByTimecampName, setUnmatchedRemediationByTimecampName] = useState({});

  const { data: usersRes } = useUsers();
  const { data: closeDatesRes } = useCloseDates();

  const closeDates = useMemo(() => {
    const list = Array.isArray(closeDatesRes) ? closeDatesRes : closeDatesRes?.rows;
    return Array.isArray(list) ? list : [];
  }, [closeDatesRes]);

  const usersList = useMemo(() => {
    const list = Array.isArray(usersRes) ? usersRes : usersRes?.users;
    const arr = Array.isArray(list) ? list : [];
    return arr
      .map((u) => ({
        id: Number(u?.id),
        name: String(u?.name || '').trim(),
        email: u?.email ? String(u.email).trim() : '',
        ftePercent: Number(u?.ftePercent ?? u?.fte_percent ?? 100),
      }))
      .filter((u) => Number.isInteger(u.id) && u.name);
  }, [usersRes]);

  const userOptions = useMemo(
    () =>
      usersList
        .map((u) => ({ label: u.name, value: u.id }))
        .sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [usersList]
  );

  const userNameById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), u.name);
    }
    return map;
  }, [usersList]);

  const userEmailById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), u.email || '');
    }
    return map;
  }, [usersList]);

  const userFteById = useMemo(() => {
    const map = new Map();
    for (const u of usersList) {
      map.set(Number(u.id), Number.isFinite(Number(u.ftePercent)) ? Number(u.ftePercent) : 100);
    }
    return map;
  }, [usersList]);

  const unmatchedActionOptions = useMemo(
    () => [
      {
        label: 'Select action',
        value: null,
        description: '',
      },
      {
        label: 'Add to task + accept',
        value: 'add',
        description: 'Adds this person to the task roster and pulls their hours into Actuals.',
      },
      {
        label: 'Kick back (send email)',
        value: 'kickback',
        description: 'Sends an email asking them to log time to the right task.',
      },
      {
        label: 'Accept (not assigned to task)',
        value: 'unassigned',
        description: 'Pulls hours into Actuals for the matched user, but does not add them to the task roster.',
      },
    ],
    []
  );

  const getUnmatchedActionLabel = useCallback(
    (action) => {
      const found = unmatchedActionOptions.find((o) => o.value === action);
      return found?.label || 'Select action';
    },
    [unmatchedActionOptions]
  );

  const renderUnmatchedActionMenuFor = useCallback(
    (timecampName) =>
      ({ onClose, left, top, className, ...rest }, ref) => {
        const tc = String(timecampName || '').trim();
        return (
          <Popover
            {...rest}
            ref={ref}
            className={className}
            full
            style={{ left, top, padding: 0, zIndex: 3000 }}
          >
            <Dropdown.Menu
              onSelect={(eventKey) => {
                const v = eventKey;
                const val = v === 'add' || v === 'kickback' || v === 'unassigned' ? v : null;
                setUnmatchedRemediationByTimecampName((prev) => ({
                  ...prev,
                  [tc]: { ...prev?.[tc], action: val, error: null },
                }));
                if (typeof onClose === 'function') onClose();
              }}
            >
              {unmatchedActionOptions
                .filter((o) => o.value !== null)
                .map((opt) => (
                  <Dropdown.Item key={String(opt.value)} eventKey={opt.value} description={opt.description}>
                    {opt.label}
                  </Dropdown.Item>
                ))}
            </Dropdown.Menu>
          </Popover>
        );
      },
    [unmatchedActionOptions]
  );

  const [matchModalOpen, setMatchModalOpen] = useState(false);
  const [matchModalTimecampName, setMatchModalTimecampName] = useState(null);
  const [matchModalUserId, setMatchModalUserId] = useState(null);

  useEffect(() => {
    if (!unmatchedModalOpen) return;

    // Initialize per-user remediation state when the modal opens.
    setUnmatchedRemediationByTimecampName((prev) => {
      const next = { ...prev };
      for (const r of unmatchedModalRows) {
        const tc = String(r?.userName || '').trim();
        if (!tc) continue;
        if (next[tc]) continue;

        const match = findBestUserMatchByName(tc, usersList);
        const suggestedUserId = Number.isInteger(match.userId) ? Number(match.userId) : null;
        next[tc] = {
          // Default to no action selected. (We still prefill the suggested match to reduce clicks.)
          action: null,
          userId: suggestedUserId,
          resolved: false,
          busy: false,
          error: null,
          autoMatched: Boolean(suggestedUserId),
          autoMatchReason: match.reason,
        };
      }
      return next;
    });
  }, [unmatchedModalOpen, unmatchedModalRows, usersList]);

  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [clearMonth, setClearMonth] = useState(null);

  const clearTaskActualsForMonth = useClearTaskActualsForMonth({
    onSuccess: (_data, variables) => {
      toaster.push(
        <Message showIcon type="success" closable>
          Cleared actuals for {formatMonthLabel(variables?.monthStart)}.
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );
    },
  });

  const pullTimecampHoursForMonth = useCallback(
    async (monthStart) => {
      if (!isTimeCampActive) return;
      if (isOdcTask) return;
      if (!canPull) return;
      if (!isIsoDate(monthStart)) return;
      if (!Number.isInteger(Number(timecampTaskId))) return;

      setPullingMonth(monthStart);

      try {
        const resp = await authenticatedFetch(pullHoursUrlBase, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthStart }),
        });

        const json = await resp.json().catch(() => null);
        if (!resp.ok) {
          throw new Error(json?.error || `Failed to pull hours (${resp.status})`);
        }

        toaster.push(
          <Message showIcon type="success" closable>
            Hours pulled and saved for {formatMonthLabel(monthStart)}.
          </Message>,
          { placement: 'topEnd', duration: 2400 }
        );

        setRefreshAttemptedAtByMonth((prev) => ({
          ...prev,
          [monthStart]: normalizeIsoTimestamp(json?.refreshedAt) || new Date().toISOString(),
        }));

        const detailsRaw = Array.isArray(json?.unmatchedUsersDetails) ? json.unmatchedUsersDetails : [];
        const details = detailsRaw
          .map((r) => ({
            userName: typeof r?.userName === 'string' ? r.userName.trim() : '',
            hours: Number(r?.hours ?? 0),
          }))
          .filter((r) => r.userName && Number.isFinite(r.hours) && r.hours > 0)
          .sort((a, b) => String(a.userName).localeCompare(String(b.userName)));

        const entriesRaw = Array.isArray(json?.unmatchedEntries) ? json.unmatchedEntries : [];
        const entries = entriesRaw
          .map((e) => ({
            userName: typeof e?.userName === 'string' ? e.userName.trim() : '',
            date: typeof e?.date === 'string' ? e.date.slice(0, 10) : '',
            hours: Number(e?.hours ?? 0),
            description: e?.description ? String(e.description) : '',
            externalId: e?.externalId ? String(e.externalId) : null,
          }))
          .filter((e) => e.userName && isIsoDate(e.date) && Number.isFinite(e.hours) && e.hours > 0);

        if (details.length > 0) {
          setUnmatchedModalMonth(monthStart);
          setUnmatchedModalRows(details);
          setUnmatchedModalEntries(entries);
          setUnmatchedRemediationByTimecampName({});
          setUnmatchedModalOpen(true);
        } else {
          const unknownUsers = Array.isArray(json?.unmatchedUsers) ? json.unmatchedUsers : [];
          if (unknownUsers.length > 0) {
            const uniq = Array.from(new Set(unknownUsers)).slice(0, 6);
            toaster.push(
              <Message showIcon type="warning" closable>
                Could not match some TimeCamp users: {uniq.join(', ')}{unknownUsers.length > uniq.length ? '…' : ''}
              </Message>,
              { placement: 'topEnd', duration: 6000 }
            );
          }
        }

        if (typeof refetchTaskActuals === 'function') {
          await refetchTaskActuals();
        }
      } catch (e) {
        toaster.push(
          <Message showIcon type="error" closable>
            Failed to pull TimeCamp entries for {formatMonthLabel(monthStart)}: {e?.message || String(e)}
          </Message>,
          { placement: 'topEnd', duration: 7000 }
        );
      } finally {
        setPullingMonth(null);
      }
    },
    [authenticatedFetch, canPull, isOdcTask, isTimeCampActive, pullHoursUrlBase, refetchTaskActuals, timecampTaskId]
  );

  const didAutoPullRef = useRef(false);
  useEffect(() => {
    if (didAutoPullRef.current) return;
    if (!isIsoDate(autoPullMonthStart)) return;
    if (!isTimeCampActive) return;
    if (!canPull) return;
    if (pullingMonth !== null) return;

    const isFutureMonth = String(autoPullMonthStart).slice(0, 7) > String(currentMonthStart).slice(0, 7);
    if (isFutureMonth) return;

    // Ensure the month is visible in the current window.
    if (!months.includes(autoPullMonthStart)) {
      setStartMonth(autoPullMonthStart);
    }

    didAutoPullRef.current = true;
    pullTimecampHoursForMonth(autoPullMonthStart);
  }, [autoPullMonthStart, canPull, currentMonthStart, isTimeCampActive, months, pullingMonth, pullTimecampHoursForMonth]);

  const acceptUnmatchedUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/actuals/accept-unmatched`;
  }, [taskId]);

  const kickbackSendUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/timecamp/kickback-send`;
  }, [taskId]);

  const totalUnmatchedHours = useMemo(
    () => unmatchedModalRows.reduce((sum, row) => sum + Number(row?.hours ?? 0), 0),
    [unmatchedModalRows]
  );

  const unmatchedProgress = useMemo(() => {
    const total = unmatchedModalRows.length;
    const resolved = unmatchedModalRows.reduce((acc, r) => {
      const tc = String(r?.userName || '').trim();
      if (!tc) return acc;
      const st = unmatchedRemediationByTimecampName?.[tc];
      return acc + (st?.resolved ? 1 : 0);
    }, 0);
    return { total, resolved };
  }, [unmatchedModalRows, unmatchedRemediationByTimecampName]);

  const anyUnmatchedBusy = useMemo(
    () =>
      unmatchedModalRows.some((r) => {
        const tc = String(r?.userName || '').trim();
        return Boolean(unmatchedRemediationByTimecampName?.[tc]?.busy);
      }),
    [unmatchedModalRows, unmatchedRemediationByTimecampName]
  );

  const allUnmatchedResolved = useMemo(
    () =>
      unmatchedModalRows.every((r) => {
        const tc = String(r?.userName || '').trim();
        return Boolean(unmatchedRemediationByTimecampName?.[tc]?.resolved);
      }),
    [unmatchedModalRows, unmatchedRemediationByTimecampName]
  );

  useEffect(() => {
    if (!unmatchedModalOpen) return;
    if (anyUnmatchedBusy) return;
    if (!allUnmatchedResolved) return;
    setUnmatchedModalOpen(false);
  }, [allUnmatchedResolved, anyUnmatchedBusy, unmatchedModalOpen]);

  const applyRemediationForTimecampUser = useCallback(
    async (timecampUserName, options = {}) => {
      if (!unmatchedModalMonth) return;
      const tc = String(timecampUserName || '').trim();
      if (!tc) return;

      const st = unmatchedRemediationByTimecampName?.[tc];
      if (!st || st.resolved || st.busy) return;

      const action = st.action;
      if (!action) {
        setUnmatchedRemediationByTimecampName((prev) => ({
          ...prev,
          [tc]: { ...prev?.[tc], error: 'Select an action first.' },
        }));
        return;
      }

      const row = unmatchedModalRows.find((r) => String(r?.userName || '').trim() === tc);
      const overrideHours = Number(options?.hoursOverride);
      const rowHours = Number.isFinite(overrideHours) ? overrideHours : Number(row?.hours ?? 0);

      setUnmatchedRemediationByTimecampName((prev) => ({
        ...prev,
        [tc]: { ...prev?.[tc], busy: true, error: null },
      }));

      try {
        if (action === 'add') {
          const userId = Number(st.userId);
          if (!Number.isInteger(userId)) {
            throw new Error('Select an app user mapping first.');
          }

          const resp = await authenticatedFetch(pullHoursUrlBase, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              monthStart: unmatchedModalMonth,
              manualUserMappings: [{ timecampUserName: tc, userId }],
            }),
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            throw new Error(json?.error || `Failed to pull hours (${resp.status})`);
          }

          const detailsRaw = Array.isArray(json?.unmatchedUsersDetails) ? json.unmatchedUsersDetails : [];
          const details = detailsRaw
            .map((r) => ({
              userName: typeof r?.userName === 'string' ? r.userName.trim() : '',
              hours: Number(r?.hours ?? 0),
            }))
            .filter((r) => r.userName && Number.isFinite(r.hours) && r.hours > 0)
            .sort((a, b) => String(a.userName).localeCompare(String(b.userName)));

          const entriesRaw = Array.isArray(json?.unmatchedEntries) ? json.unmatchedEntries : [];
          const entries = entriesRaw
            .map((e) => ({
              userName: typeof e?.userName === 'string' ? e.userName.trim() : '',
              date: typeof e?.date === 'string' ? e.date.slice(0, 10) : '',
              hours: Number(e?.hours ?? 0),
              description: e?.description ? String(e.description) : '',
              externalId: e?.externalId ? String(e.externalId) : null,
            }))
            .filter((e) => e.userName && isIsoDate(e.date) && Number.isFinite(e.hours) && e.hours > 0);

          const stillUnmatched = details.some((d) => d.userName === tc);
          if (stillUnmatched) {
            throw new Error('Name match still failed after mapping. Double-check the selected app user.');
          }

          const supportersAdded = Number(json?.supportersAdded ?? 0);
          toaster.push(
            <Message showIcon type="success" closable>
              Accepted hours for {tc}{supportersAdded > 0 ? ` (supports added: ${supportersAdded})` : ''}.
            </Message>,
            { placement: 'topEnd', duration: 2600 }
          );

          setUnmatchedModalRows(details);
          setUnmatchedModalEntries(entries);
          setUnmatchedRemediationByTimecampName((prev) => ({
            ...prev,
            [tc]: { ...prev?.[tc], resolved: true, busy: false, error: null },
          }));

          if (typeof refetchTaskActuals === 'function') {
            await refetchTaskActuals();
          }
          return;
        }

        if (action === 'unassigned') {
          const userId = Number(st.userId);
          if (!Number.isInteger(userId)) {
            throw new Error('Select an app user mapping first.');
          }
          if (!Number.isFinite(rowHours) || rowHours <= 0) {
            throw new Error('No hours to accept for this user.');
          }

          const resp = await authenticatedFetch(acceptUnmatchedUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              monthStart: unmatchedModalMonth,
              userId,
              hours: rowHours,
              mode: 'set',
              timecampUserName: tc,
            }),
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            throw new Error(json?.error || `Failed to accept unmatched hours (${resp.status})`);
          }

          toaster.push(
            <Message showIcon type="success" closable>
              Accepted {formatHours(rowHours)} for {tc} (not assigned to task).
            </Message>,
            { placement: 'topEnd', duration: 2600 }
          );

          setUnmatchedRemediationByTimecampName((prev) => ({
            ...prev,
            [tc]: { ...prev?.[tc], resolved: true, busy: false, error: null },
          }));

          if (typeof refetchTaskActuals === 'function') {
            await refetchTaskActuals();
          }
          return;
        }

        if (action === 'kickback') {
          const userId = Number(st.userId);
          if (!Number.isInteger(userId)) {
            throw new Error('Select an app user mapping first.');
          }
          const recipientEmail = String(userEmailById.get(userId) || '').trim();
          if (!recipientEmail) {
            throw new Error('Mapped app user is missing an email address.');
          }

          const entries = unmatchedModalEntries.filter((e) => e.userName === tc).slice(0, 200);

          const resp = await authenticatedFetch(kickbackSendUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              monthStart: unmatchedModalMonth,
              recipients: [{ timecampUserName: tc, to: recipientEmail, entries }],
            }),
          });

          const json = await resp.json().catch(() => null);
          if (!resp.ok) {
            throw new Error(json?.error || `Failed to send email (${resp.status})`);
          }

          const sendResults = Array.isArray(json?.sendResults) ? json.sendResults : [];
          const sentForUser = sendResults.find((r) => String(r?.timecampUserName || '').trim() === tc);
          if (!sentForUser?.sent) {
            throw new Error(sentForUser?.error || 'Kickback email was not sent.');
          }


          setUnmatchedRemediationByTimecampName((prev) => ({
            ...prev,
            [tc]: { ...prev?.[tc], resolved: true, busy: false, error: null },
          }));

          toaster.push(
            <Message showIcon type="success" closable>
              Kickback email sent to {recipientEmail} for {tc}.
            </Message>,
            { placement: 'topEnd', duration: 2600 }
          );
          return;
        }
      } catch (e) {
        setUnmatchedRemediationByTimecampName((prev) => ({
          ...prev,
          [tc]: { ...prev?.[tc], busy: false, error: e?.message || String(e) },
        }));

        toaster.push(
          <Message showIcon type="error" closable>
            Failed to remediate {tc}: {e?.message || String(e)}
          </Message>,
          { placement: 'topEnd', duration: 7000 }
        );
      }
    },
    [
      acceptUnmatchedUrl,
      authenticatedFetch,
      kickbackSendUrl,
      pullHoursUrlBase,
      refetchTaskActuals,
      unmatchedModalEntries,
      unmatchedModalMonth,
      unmatchedModalRows,
      unmatchedRemediationByTimecampName,
      userEmailById,
    ]
  );

  const unmatchedApplyAllState = useMemo(() => {
    const rows = Array.isArray(unmatchedModalRows) ? unmatchedModalRows : [];
    const canApplyTcs = [];
    const missing = [];
    const actionByTc = new Map();

    for (const r of rows) {
      const tc = String(r?.userName || '').trim();
      if (!tc) continue;
      const st = unmatchedRemediationByTimecampName?.[tc] || {};
      const action = st?.action ?? null;
      const mappedUserId = st?.userId ?? null;
      const resolved = Boolean(st?.resolved);
      const busy = Boolean(st?.busy);

      actionByTc.set(tc, action);

      const hasAction = Boolean(action);
      const needsUser = action === 'add' || action === 'unassigned';
      const hasUser = Number.isInteger(Number(mappedUserId));
      const isReady = hasAction && (!needsUser || hasUser);
      if (!isReady) missing.push(tc);

      const canApply = !resolved && !busy && isReady;
      if (canApply) canApplyTcs.push(tc);
    }

    const allReady = rows.length > 0 && missing.length === 0;
    return { allReady, canApplyTcs, actionByTc };
  }, [unmatchedModalRows, unmatchedRemediationByTimecampName]);

  const applyAllUnmatchedRemediations = useCallback(async () => {
    if (!unmatchedModalOpen) return;
    if (anyUnmatchedBusy) return;

    if (!unmatchedApplyAllState.allReady) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Assign an action (and app user when required) for each row.
        </Message>,
        { placement: 'topEnd', duration: 4200 }
      );
      return;
    }

    // Snapshot hours at click-time so “unassigned” can apply even if a later “add” pull refreshes the list.
    const hoursByTc = new Map(
      (Array.isArray(unmatchedModalRows) ? unmatchedModalRows : [])
        .map((r) => [String(r?.userName || '').trim(), Number(r?.hours ?? 0)])
        .filter(([tc]) => tc)
    );

    const priority = (action) => {
      if (action === 'unassigned') return 0;
      if (action === 'kickback') return 1;
      if (action === 'add') return 2;
      return 3;
    };

    const ordered = [...unmatchedApplyAllState.canApplyTcs].sort((a, b) => {
      const pa = priority(unmatchedApplyAllState.actionByTc.get(a));
      const pb = priority(unmatchedApplyAllState.actionByTc.get(b));
      if (pa !== pb) return pa - pb;
      return String(a).localeCompare(String(b));
    });

    for (const tc of ordered) {
      // applyRemediationForTimecampUser manages its own busy/resolved guards.
      // eslint-disable-next-line no-await-in-loop
      await applyRemediationForTimecampUser(tc, { hoursOverride: hoursByTc.get(tc) });
    }
  }, [anyUnmatchedBusy, applyRemediationForTimecampUser, unmatchedApplyAllState, unmatchedModalOpen, unmatchedModalRows]);

  const openMatchModalForTimecampUser = useCallback(
    (timecampUserName) => {
      const tc = String(timecampUserName || '').trim();
      if (!tc) return;
      const st = unmatchedRemediationByTimecampName?.[tc];
      const current = st && Number.isInteger(Number(st.userId)) ? Number(st.userId) : null;
      setMatchModalTimecampName(tc);
      setMatchModalUserId(current);
      setMatchModalOpen(true);
    },
    [unmatchedRemediationByTimecampName]
  );

  const saveMatchModal = useCallback(() => {
    const tc = String(matchModalTimecampName || '').trim();
    const uid = Number(matchModalUserId);
    if (!tc) return;
    if (!Number.isInteger(uid)) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Select an app user to match.
        </Message>,
        { placement: 'topEnd', duration: 4000 }
      );
      return;
    }

    setUnmatchedRemediationByTimecampName((prev) => ({
      ...prev,
      [tc]: {
        ...prev?.[tc],
        userId: uid,
        error: null,
        autoMatched: false,
        autoMatchReason: 'manual',
      },
    }));

    setMatchModalOpen(false);
  }, [matchModalTimecampName, matchModalUserId]);

  const upsertTaskActualsUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/tasks/${encodeURIComponent(String(taskId || ''))}/actuals`;
  }, [taskId]);

  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [savingCell, setSavingCell] = useState(false);

  const editingCellRef = useRef(null);
  const editingValueRef = useRef(null);
  const inlineEditorRef = useRef(null);

  useEffect(() => {
    editingCellRef.current = editingCell;
  }, [editingCell]);

  useEffect(() => {
    editingValueRef.current = editingValue;
  }, [editingValue]);

  const beginEdit = useCallback(
    (userId, monthStart, currentHours) => {
      if (!allowManualEdit) return;
      if (savingCell) return;
      if (!Number.isInteger(Number(userId))) return;
      if (!isIsoDate(monthStart)) return;
      setEditingCell({ userId: Number(userId), monthStart });
      setEditingValue(Number.isFinite(Number(currentHours)) ? Number(currentHours) : 0);
    },
    [allowManualEdit, savingCell]
  );

  const cancelEdit = useCallback(() => {
    setEditingCell(null);
    setEditingValue(null);
  }, []);

  const closeEditorIfSameCell = useCallback((cell) => {
    if (!cell) return;
    setEditingCell((prev) => {
      if (!prev) return prev;
      const same = Number(prev.userId) === Number(cell.userId) && String(prev.monthStart) === String(cell.monthStart);
      if (!same) return prev;
      setEditingValue(null);
      return null;
    });
  }, []);

  const saveEdit = useCallback(async (overrideCell = null, overrideValue = null) => {
    const cell = overrideCell ?? editingCell;
    const value = overrideValue ?? editingValue;
    if (!cell) return;
    if (!allowManualEdit) return;
    if (!Number.isInteger(taskId)) return;

    const userId = Number(cell.userId);
    const monthStart = cell.monthStart;
    const hours = Number(value);
    const normalized = Number.isFinite(hours) ? Math.max(0, hours) : 0;

    // If unchanged, just close the editor quietly.
    const current = Number(actualHoursByUserId.get(userId)?.[monthStart] ?? 0);
    if (Number.isFinite(current) && normalized === current) {
      closeEditorIfSameCell(cell);
      return;
    }

    setSavingCell(true);
    try {
      const resp = await authenticatedFetch(upsertTaskActualsUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ monthStart, rows: [{ userId, hours: normalized }] }),
      });

      const json = await resp.json().catch(() => null);
      if (!resp.ok) {
        throw new Error(json?.error || `Failed to save hours (${resp.status})`);
      }

      toaster.push(
        <Message showIcon type="success" closable>
          Saved actual hours for {formatMonthLabel(monthStart)}.
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );

      if (typeof refetchTaskActuals === 'function') {
        await refetchTaskActuals();
      }

      closeEditorIfSameCell(cell);
    } catch (e) {
      toaster.push(
        <Message showIcon type="error" closable>
          Failed to save actual hours: {e?.message || String(e)}
        </Message>,
        { placement: 'topEnd', duration: 7000 }
      );
    } finally {
      setSavingCell(false);
    }
  }, [actualHoursByUserId, allowManualEdit, authenticatedFetch, closeEditorIfSameCell, editingCell, editingValue, refetchTaskActuals, taskId, upsertTaskActualsUrl]);

  // Save when clicking anywhere outside the inline editor.
  useEffect(() => {
    if (!allowManualEdit) return undefined;
    if (!editingCell) return undefined;

    const onPointerDownCapture = (e) => {
      const cell = editingCellRef.current;
      if (!cell) return;
      const root = inlineEditorRef.current;
      if (root && e?.target && root.contains(e.target)) return;

      // If the user clicks elsewhere, commit current value.
      const v = editingValueRef.current;
      void saveEdit({ ...cell }, v);
    };

    document.addEventListener('pointerdown', onPointerDownCapture, true);
    return () => document.removeEventListener('pointerdown', onPointerDownCapture, true);
  }, [allowManualEdit, editingCell, saveEdit]);

  const openClearModalForMonth = useCallback(
    (monthStart) => {
      if (!isIsoDate(monthStart)) return;
      setClearMonth(monthStart);
      setClearModalOpen(true);
    },
    []
  );

  return (
    <>
    <div className="ppc-noprint" style={{ overflowX: 'auto', paddingBottom: 6 }}>
      <div className="ppc-actuals-table-header">
        <div className="ppc-actuals-table-header-left">
          <ProviderBadge provider={provider} isLoading={providerLoading} />
        </div>
        <div className="ppc-actuals-table-header-right">
          <div className="ppc-actuals-range-row">
            <IconButton
              appearance="subtle"
              size="sm"
              icon={<i className="bi bi-chevron-left" aria-hidden="true" />}
              aria-label="Previous months"
              title="Previous months"
              onClick={goPrevMonths}
            />
            <div className="ppc-actuals-range-label">{monthRangeLabel}</div>
            <IconButton
              appearance="subtle"
              size="sm"
              icon={<i className="bi bi-chevron-right" aria-hidden="true" />}
              aria-label="Next months"
              title="Next months"
              onClick={goNextMonths}
            />
            <Button appearance="subtle" size="sm" onClick={goCurrentMonths} disabled={isOnCurrentWindow}>
              Current
            </Button>

            <Button appearance="subtle" size="sm" onClick={handleExportXlsx} disabled={taskHourForecastLoading || taskActualsLoading}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <FileExcelO aria-hidden="true" />
                Export
              </span>
            </Button>

            <Button appearance="subtle" size="sm" onClick={() => setPrintMode(true)} disabled={printMode}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <i className="bi bi-printer" aria-hidden="true" />
                Print
              </span>
            </Button>
          </div>
        </div>
      </div>

      <div
        style={{
          minWidth: 820,
          display: 'grid',
          gridTemplateColumns: colTemplate,
          gap: 0,
          alignItems: 'stretch',
        }}
      >
        <div style={{ fontWeight: 800, padding: '10px 10px', borderBottom: '1px solid #e6e6e6' }}>Person</div>
        {months.map((m) => {
          const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
          const pullDisabled = !isTimeCampActive || !canPull || pullingMonth !== null || isFutureMonth;
          const hasStoredFromApi = Array.isArray(taskActuals?.monthsWithRows)
            ? taskActuals.monthsWithRows.includes(m)
            : false;
          const localRefreshAt = refreshAttemptedAtByMonth?.[m] || null;
          const hasStored = hasStoredFromApi || Boolean(monthLastModifiedByMonth?.[m]) || Boolean(localRefreshAt);
          const refreshedAt = latestIsoTimestamp(monthLastModifiedByMonth?.[m], localRefreshAt);
          const refreshedDaysLabel = formatRefreshedLabel(refreshedAt);
          const pullTitle = isOdcTask
            ? 'TimeCamp pull is not applicable for ODC tasks'
            : !canPull
            ? 'Link a TimeCamp task to enable pulling hours'
            : isFutureMonth
              ? 'Cannot pull TimeCamp hours for future months'
              : `Pull TimeCamp hours for ${formatMonthLabel(m)}`;

          return (
            <div
              key={m}
              className="ppc-month-header"
              style={{
                padding: '10px 10px',
                borderBottom: '1px solid #e6e6e6',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <div className="ppc-month-title-row">
                <div className="ppc-month-title" style={{ fontWeight: 800 }}>{formatMonthLabel(m)}</div>
                <div className="ppc-month-delete-wrap">
                  <Whisper
                    placement="top"
                    speaker={<Tooltip>Clear saved actuals for this month…</Tooltip>}
                    trigger={['hover', 'focus']}
                  >
                    <IconButton
                      className="ppc-month-delete"
                      appearance="subtle"
                      size="xs"
                      icon={<i className="bi bi-trash" aria-hidden="true" />}
                      aria-label={`Clear actuals for ${formatMonthLabel(m)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openClearModalForMonth(m);
                      }}
                      disabled={!Number.isInteger(taskId) || clearTaskActualsForMonth.isPending}
                    />
                  </Whisper>
                </div>
              </div>
              {isTimeCampActive && !isOdcTask ? (
                <>
                  <div style={{ marginTop: 6, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      appearance="ghost"
                      size="xs"
                      loading={pullingMonth === m}
                      disabled={pullDisabled}
                      title={pullTitle}
                      onClick={() => pullTimecampHoursForMonth(m)}
                    >
                      {hasStored ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <i className="bi bi-arrow-repeat" aria-hidden="true" />
                          Refresh
                        </span>
                      ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <i className="bi bi-arrow-down" aria-hidden="true" />
                          Pull
                        </span>
                      )}
                    </Button>
                  </div>
                  {refreshedDaysLabel ? (
                    <div style={{ marginTop: 4, fontSize: 11, color: '#888', lineHeight: '14px' }}>
                      Refreshed {refreshedDaysLabel}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          );
        })}

        {people.map((p) => (
          <div key={p.userId ?? p.name} style={{ display: 'contents' }}>
            <div
              style={{
                padding: '10px 10px',
                borderBottom: '1px solid #f0f0f0',
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={p.name}
            >
              {(() => {
                const timecardUpdatedLabel = formatDaysAgoLabel(p.timecardLastModifiedAt);
                return (
                  <>
              <Button
                appearance="link"
                size="xs"
                style={{
                  padding: 0,
                  fontWeight: 700,
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
                title={taskForecastPath ? 'Go to task forecast' : undefined}
                disabled={!taskForecastPath}
                onClick={() => {
                  if (!taskForecastPath) return;
                  if (typeof onOpenTaskForecast === 'function') {
                    onOpenTaskForecast(taskForecastPath, {
                      fromActuals: true,
                      actualsPath: actualsReturnPath || null,
                    });
                  }
                }}
              >
                {p.name}
              </Button>
              {p.notAssigned ? (
                <div style={{ fontSize: 11, color: '#888', lineHeight: '14px' }}>Not assigned to task</div>
              ) : null}
              {timecardUpdatedLabel ? (
                <div style={{ fontSize: 11, color: '#888', lineHeight: '14px' }}>
                  Timecard updated {timecardUpdatedLabel}
                </div>
              ) : null}
                  </>
                );
              })()}
            </div>
            {months.map((m) => (
              <div
                key={`${p.userId ?? p.name}|${m}`}
                className={allowManualEdit && Number.isInteger(Number(p.userId)) ? 'ppc-actuals-cell-editable' : undefined}
                style={{
                  padding: '10px 10px',
                  borderBottom: '1px solid #f0f0f0',
                  textAlign: 'center',
                  fontVariantNumeric: 'tabular-nums',
                }}
                title={allowManualEdit && Number.isInteger(Number(p.userId)) ? 'Click to edit actual hours' : undefined}
                onClick={() => {
                  if (!allowManualEdit) return;
                  const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                  if (isFutureMonth) return;
                  const uid = Number(p.userId);
                  if (!Number.isInteger(uid)) return;
                  const act = Number(actualHoursByUserId.get(uid)?.[m] ?? 0);
                  beginEdit(uid, m, act);
                }}
              >
                {(() => {
                  const uid = Number(p.userId);
                  const hasUser = Number.isInteger(uid);
                  const proj = hasUser ? Number(forecastHoursByUserId.get(uid)?.[m] ?? 0) : null;
                  const act = hasUser ? Number(actualHoursByUserId.get(uid)?.[m] ?? 0) : null;
                  const showDelta = hasUser && !taskHourForecastLoading && !taskActualsLoading;
                  const delta = showDelta ? Number(act ?? 0) - Number(proj ?? 0) : null;
                  const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);
                  const shortfallWarning =
                    showDelta &&
                    !isFutureMonth &&
                    shouldWarnProjectedShortfall({
                      monthStart: m,
                      projected: proj,
                      actual: act,
                      currentMonthStart,
                      ftePercent: userFteById.get(uid) ?? 100,
                      closeDates,
                    });

                  const isEditing =
                    allowManualEdit &&
                    hasUser &&
                    editingCell &&
                    Number(editingCell.userId) === uid &&
                    String(editingCell.monthStart) === String(m);

                  return (
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          color: '#666',
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'center',
                          gap: 12,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <span>
                          <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                          {hasUser ? (taskHourForecastLoading ? '…' : formatHours(proj)) : '—'}
                        </span>
                        {!isFutureMonth ? (
                          <span>
                            <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                            {hasUser ? (
                              taskActualsLoading ? (
                                '…'
                              ) : isEditing ? (
                                <span
                                  ref={inlineEditorRef}
                                  style={{ display: 'inline-flex', alignItems: 'center' }}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDownCapture={(e) => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const cell = editingCellRef.current;
                                      if (!cell) return;
                                      void saveEdit({ ...cell }, editingValueRef.current);
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      cancelEdit();
                                    }
                                  }}
                                >
                                  <InputNumber
                                    size="xs"
                                    min={0}
                                    step={0.25}
                                    value={editingValue}
                                    onChange={(v) => setEditingValue(v)}
                                    disabled={savingCell}
                                    style={{ width: 90 }}
                                  />
                                </span>
                              ) : (
                                <span className="ppc-actuals-act-value">
                                  {formatHours(act)}
                                  {allowManualEdit ? (
                                    <i className="bi bi-pencil" aria-hidden="true" style={{ fontSize: 12, color: '#888' }} />
                                  ) : null}
                                </span>
                              )
                            ) : (
                              '—'
                            )}
                          </span>
                        ) : null}
                      </div>
                      <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                      <div style={{ marginTop: 6, height: 18, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {showDelta && !isFutureMonth ? (
                          <DeltaBadge delta={delta} projected={proj} actual={act} shortfallWarning={shortfallWarning} />
                        ) : null}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        ))}

        <div style={{ display: 'contents' }}>
          <div
            style={{
              padding: '10px 10px',
              borderTop: '2px solid #e6e6e6',
              fontWeight: 800,
            }}
          >
            Total
          </div>

          {months.map((m) => (
            <div
              key={`totals|${m}`}
              style={{
                padding: '10px 10px',
                borderTop: '2px solid #e6e6e6',
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {(() => {
                const proj = Number(taskHourForecast?.totals?.[m] ?? 0);
                const act = Number(taskActuals?.totals?.[m] ?? 0);
                const showDelta = !taskHourForecastLoading && !taskActualsLoading;
                const delta = showDelta ? act - proj : null;
                const isFutureMonth = String(m).slice(0, 7) > String(currentMonthStart).slice(0, 7);

                return (
                  <div>
                    <div
                      style={{
                        fontSize: 12,
                        color: '#444',
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        gap: 12,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span>
                        <span style={{ fontWeight: 800, marginRight: 4 }}>Proj</span>
                        {taskHourForecastLoading ? '…' : formatHours(proj)}
                      </span>
                      {!isFutureMonth ? (
                        <span>
                          <span style={{ fontWeight: 800, marginRight: 4 }}>Act</span>
                          {taskActualsLoading ? '…' : formatHours(act)}
                        </span>
                      ) : null}
                    </div>
                    <div style={{ marginTop: 6, height: 1, background: '#e6e6e6' }} />
                    <div style={{ marginTop: 6, height: 18, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {showDelta && !isFutureMonth ? <DeltaBadge delta={delta} projected={proj} actual={act} /> : null}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      </div>

      <Modal open={clearModalOpen} onClose={() => setClearModalOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Clear actuals</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            This will permanently delete saved actual hours for the selected month. This cannot be undone.
          </Message>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Month</div>
            <SelectPicker
              block
              searchable={false}
              cleanable={false}
              data={months.map((m) => ({ label: formatMonthLabel(m), value: m }))}
              value={clearMonth}
              onChange={(v) => setClearMonth(v)}
              placeholder="Select a month"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            color="red"
            loading={clearTaskActualsForMonth.isPending}
            disabled={!clearMonth}
            onClick={async () => {
              if (!clearMonth) return;
              await clearTaskActualsForMonth.mutateAsync({ taskId, monthStart: clearMonth });
              setClearModalOpen(false);
            }}
          >
            Clear
          </Button>
          <Button
            appearance="subtle"
            onClick={() => setClearModalOpen(false)}
            disabled={clearTaskActualsForMonth.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        open={unmatchedModalOpen}
        onClose={() => {
          if (anyUnmatchedBusy) return;
          setUnmatchedModalOpen(false);
        }}
        backdropStyle={{ zIndex: 1030 }}
        style={{ zIndex: 1040 }}
        size="md"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header>
          <Modal.Title>Unmatched TimeCamp users</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Message type="warning" showIcon>
            These users logged time in TimeCamp for this task in {formatMonthLabel(unmatchedModalMonth)}, but they aren’t
            currently assigned as a lead/supporter on this task (or their name didn’t match). Add them to the task roster
            to include their hours when pulling.
          </Message>

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Details</div>
            <div style={{ border: '1px solid #e6e6e6', borderRadius: 8, overflow: 'hidden' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px',
                  gap: 0,
                  background: '#fafafa',
                  borderBottom: '1px solid #e6e6e6',
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                <div style={{ padding: '8px 10px' }}>TimeCamp user</div>
                <div style={{ padding: '8px 10px', textAlign: 'right' }}>Hours</div>
              </div>

              {unmatchedModalRows.map((r) => (
                <div
                  key={r.userName}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 110px',
                    gap: 0,
                    borderBottom: '1px solid #f0f0f0',
                    fontSize: 13,
                  }}
                >
                  <div style={{ padding: '8px 10px', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.userName}>
                    {r.userName}
                  </div>
                  <div style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatHours(r.hours)}
                  </div>
                </div>
              ))}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 110px',
                  gap: 0,
                  background: '#fff',
                  fontWeight: 800,
                }}
              >
                <div style={{ padding: '8px 10px' }}>Total</div>
                <div style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {formatHours(totalUnmatchedHours)}
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <Message type="info" showIcon>
              Remediate each user independently. You can add someone to the task + accept, send a kickback email,
              or accept hours without adding them to the task roster.
            </Message>

            <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
              Progress: {unmatchedProgress.resolved} / {unmatchedProgress.total} remediated
            </div>

            <div style={{ marginTop: 10, border: '1px solid #e6e6e6', borderRadius: 8, overflow: 'visible' }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '260px 1fr 120px',
                  gap: 0,
                  background: '#fafafa',
                  borderBottom: '1px solid #e6e6e6',
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                <div style={{ padding: '8px 10px' }}>Action</div>
                <div style={{ padding: '8px 10px' }}>App user</div>
                <div style={{ padding: '8px 10px' }} />
              </div>

              {unmatchedModalRows.map((r) => {
                const tc = String(r?.userName || '').trim();
                const st = unmatchedRemediationByTimecampName?.[tc] || {};
                const action = st?.action ?? null;
                const mappedUserId = st?.userId ?? null;
                const mappedUserName = Number.isInteger(Number(mappedUserId)) ? userNameById.get(Number(mappedUserId)) : null;
                const mappedUserEmail = Number.isInteger(Number(mappedUserId)) ? userEmailById.get(Number(mappedUserId)) : '';
                const resolved = Boolean(st?.resolved);
                const busy = Boolean(st?.busy);
                const error = st?.error ? String(st.error) : '';

                const userNotFound = !Number.isInteger(Number(mappedUserId));

                const hasMappedUser = Number.isInteger(Number(mappedUserId));
                const hasMappedEmail = Boolean(mappedUserEmail);
                const canApply =
                  !resolved &&
                  !busy &&
                  action &&
                  ((action === 'add' || action === 'unassigned') ? hasMappedUser : true) &&
                  (action === 'kickback' ? hasMappedUser && hasMappedEmail : true);

                return (
                  <div
                    key={`remediate:${tc}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '260px 1fr 120px',
                      gap: 0,
                      borderBottom: '1px solid #f0f0f0',
                      fontSize: 13,
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ padding: '8px 10px' }}>
                      <Whisper
                        placement="bottomStart"
                        trigger="click"
                        speaker={renderUnmatchedActionMenuFor(tc)}
                        container={() => document.body}
                      >
                        <Button
                          size="sm"
                          appearance="default"
                          disabled={busy || resolved || anyUnmatchedBusy}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}
                        >
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getUnmatchedActionLabel(action)}
                          </span>
                          <i className="bi bi-caret-down-fill" aria-hidden="true" style={{ fontSize: 12, color: '#888' }} />
                        </Button>
                      </Whisper>
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          {userNotFound ? (
                            <Whisper
                              placement="top"
                              speaker={<Tooltip>This user was not found in the system (click to fix)</Tooltip>}
                              trigger={['hover', 'focus']}
                            >
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={() => openMatchModalForTimecampUser(tc)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openMatchModalForTimecampUser(tc);
                                  }
                                }}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 6,
                                  cursor: busy || resolved || anyUnmatchedBusy ? 'not-allowed' : 'pointer',
                                  color: '#b00020',
                                  userSelect: 'none',
                                  opacity: busy || resolved || anyUnmatchedBusy ? 0.6 : 1,
                                }}
                                aria-disabled={busy || resolved || anyUnmatchedBusy}
                              >
                                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
                                <span style={{ fontSize: 12 }}>Not found</span>
                              </span>
                            </Whisper>
                          ) : (
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => openMatchModalForTimecampUser(tc)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openMatchModalForTimecampUser(tc);
                                }
                              }}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                cursor: busy || resolved || anyUnmatchedBusy ? 'not-allowed' : 'pointer',
                                opacity: busy || resolved || anyUnmatchedBusy ? 0.6 : 1,
                                minWidth: 0,
                              }}
                              aria-disabled={busy || resolved || anyUnmatchedBusy}
                              title="Click to change match"
                            >
                              <span
                                style={{
                                  fontWeight: 700,
                                  fontSize: 12,
                                  minWidth: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {action === 'kickback'
                                  ? (mappedUserEmail || mappedUserName || `User ${mappedUserId}`)
                                  : (mappedUserName || `User ${mappedUserId}`)}
                              </span>
                              <i className="bi bi-pencil" aria-hidden="true" style={{ fontSize: 12, color: '#888' }} />
                            </div>
                          )}

                          {resolved ? <span style={{ fontSize: 11, color: '#2e7d32', fontWeight: 700 }}>Done</span> : null}
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            color: '#666',
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={tc}
                        >
                          TimeCamp: {tc}
                        </div>

                        {st?.autoMatched && st?.autoMatchReason ? (
                          <div style={{ fontSize: 11, color: '#666' }}>Auto-matched by name</div>
                        ) : null}
                        {error ? <div style={{ fontSize: 11, color: '#b00020' }}>{error}</div> : null}
                      </div>
                    </div>
                    <div style={{ padding: '8px 10px' }}>
                      <Button
                        size="xs"
                        appearance={resolved ? 'subtle' : 'primary'}
                        disabled={!canApply || anyUnmatchedBusy}
                        loading={busy}
                        onClick={() => applyRemediationForTimecampUser(tc)}
                      >
                        {resolved ? 'Done' : 'Apply'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="subtle" disabled={anyUnmatchedBusy} onClick={() => setUnmatchedModalOpen(false)}>
            Close
          </Button>
          {unmatchedApplyAllState.allReady && !allUnmatchedResolved ? (
            <Button appearance="primary" disabled={anyUnmatchedBusy || unmatchedApplyAllState.canApplyTcs.length === 0} onClick={applyAllUnmatchedRemediations}>
              Apply all
            </Button>
          ) : (
            <Button appearance="primary" disabled={!allUnmatchedResolved || anyUnmatchedBusy} onClick={() => setUnmatchedModalOpen(false)}>
              Done
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        open={matchModalOpen}
        onClose={() => setMatchModalOpen(false)}
        size="xs"
        backdropStyle={{ zIndex: 1050 }}
        style={{ zIndex: 1060 }}
      >
        <Modal.Header>
          <Modal.Title>Match TimeCamp user</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>TimeCamp user</div>
          <div style={{ fontWeight: 800, marginBottom: 12 }}>{matchModalTimecampName}</div>

          <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Match to app user</div>
          <SelectPicker
            block
            data={userOptions}
            value={matchModalUserId}
            onChange={(v) => setMatchModalUserId(v)}
            placeholder="Select user"
            searchable
            cleanable
          />
        </Modal.Body>
        <Modal.Footer>
          <Button appearance="primary" onClick={saveMatchModal}>
            Save
          </Button>
          <Button appearance="subtle" onClick={() => setMatchModalOpen(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {!unmatchedModalOpen && unmatchedProgress.total > 0 && unmatchedProgress.resolved < unmatchedProgress.total ? (
        <div style={{ marginTop: 10 }}>
          <Message type="warning" showIcon>
            Unmatched TimeCamp users still need remediation for {formatMonthLabel(unmatchedModalMonth)} ({
              unmatchedProgress.resolved
            }/{unmatchedProgress.total}).
            <div style={{ marginTop: 8 }}>
              <Button appearance="ghost" size="xs" onClick={() => setUnmatchedModalOpen(true)}>
                Review / Remediate
              </Button>
            </div>
          </Message>
        </div>
      ) : null}

      {isTimeCampActive && !isOdcTask && !canPull ? (
        <div style={{ marginTop: 10 }}>
          <Message type="info" showIcon>
            Link a TimeCamp task to enable pulling hours.
          </Message>
        </div>
      ) : null}

      {taskHourForecastError ? (
        <div style={{ marginTop: 10 }}>
          <Message type="error" showIcon>
            Failed to load projected hours.
          </Message>
        </div>
      ) : null}

      {taskActualsError ? (
        <div style={{ marginTop: 10 }}>
          <Message type="error" showIcon>
            Failed to load actual hours.
          </Message>
        </div>
      ) : null}
    </div>

    <div className="ppc-print-area" aria-hidden={!printMode}>
      <div className="ppc-print-header">
        <div className="ppc-print-header-row">
          <div className="ppc-print-title">Actuals</div>
          <div className="ppc-print-meta">
            <div className="ppc-print-meta-item">
              Task: <span style={{ fontWeight: 700 }}>{task?.task || task?.name || `Task ${taskId}`}</span>
            </div>
            <div className="ppc-print-meta-item">
              Range: <span style={{ fontWeight: 700 }}>{monthRangeLabel}</span>
            </div>
          </div>
        </div>
      </div>

      <table className="ppc-print-table">
        <thead>
          <tr>
            <th rowSpan={2} style={{ textAlign: 'left' }}>Person</th>
            {months.map((m) => (
              <th key={`mh:${m}`} colSpan={2}>{formatMonthLabel(m)}</th>
            ))}
          </tr>
          <tr>
            {months.flatMap((m) => [
              <th key={`subp:${m}`}>Proj</th>,
              <th key={`suba:${m}`}>Act</th>,
            ])}
          </tr>
        </thead>
        <tbody>
          {people.map((p) => {
            const uid = Number(p?.userId);
            const hasUser = Number.isInteger(uid);
            const label = `${String(p?.name || '—')}${p?.notAssigned ? ' (Not assigned)' : ''}`;

            return (
              <tr key={`p:${String(p?.userId ?? p?.name)}`}> 
                <td style={{ minWidth: 220 }}>{label}</td>
                {months.map((m) => {
                  const proj = hasUser ? Number(forecastHoursByUserId.get(uid)?.[m] ?? 0) : 0;
                  const act = hasUser ? Number(actualHoursByUserId.get(uid)?.[m] ?? 0) : 0;
                  return (
                    [
                      <td key={`pp:${uid}|${m}`} className="ppc-num">{Number(proj).toFixed(2)}</td>,
                      <td key={`pa:${uid}|${m}`} className="ppc-num">{Number(act).toFixed(2)}</td>,
                    ]
                  );
                })}
              </tr>
            );
          })}

          <tr className="ppc-print-total-row">
            <td>Total</td>
            {months.flatMap((m) => [
              <td key={`tp:${m}`} className="ppc-num">{Number(taskHourForecast?.totals?.[m] ?? 0).toFixed(2)}</td>,
              <td key={`ta:${m}`} className="ppc-num">{Number(taskActuals?.totals?.[m] ?? 0).toFixed(2)}</td>,
            ])}
          </tr>
        </tbody>
      </table>
    </div>
    </>
  );
}

export default function TaskActualsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const taskId = Number(params?.taskId);
  const projectIdParam = Number(params?.projectId);
  const taskOrderIdParam = Number(params?.taskOrderId);

  const autoPullMonthStart = useMemo(() => {
    const sp = new URLSearchParams(location?.search || '');
    const v = sp.get('pullMonthStart') || sp.get('monthStart') || '';
    const iso = String(v).slice(0, 10);
    return isIsoDate(iso) ? iso : null;
  }, [location?.search]);

  const { data: activeProvider, isLoading: providerLoading } = useActiveProvider();
  const activeProviderName = activeProvider?.name || null;
  const isTimeCampActive = activeProviderName === 'timecamp';
  const isManualActive = activeProviderName === 'manual';
  void isManualActive;

  const showProviderConfigCta = !providerLoading && (!activeProviderName || activeProvider?.isConfigured === false);

  const authenticatedFetch = useAuthenticatedFetch();
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [timecampLoading, setTimecampLoading] = useState(false);
  const [timecampError, setTimecampError] = useState(null);
  const [timecampTasksTree, setTimecampTasksTree] = useState([]);
  const [timecampSelectedTaskId, setTimecampSelectedTaskId] = useState(null);
  const [timecampTreeIndex, setTimecampTreeIndex] = useState({ nodesById: new Map(), parentById: new Map() });
  const [timecampSaveLoading, setTimecampSaveLoading] = useState(false);
  const [timecampSaveError, setTimecampSaveError] = useState(null);
  const [timecampLastSavedTaskId, setTimecampLastSavedTaskId] = useState(null);

  const [linkedTimecampTaskId, setLinkedTimecampTaskId] = useState(null);
  const [linkedTimecampTaskName, setLinkedTimecampTaskName] = useState(null);

  const timecampLastRequestedTaskIdRef = useRef(null);
  const timecampSaveInFlightRef = useRef(false);

  const { data: task, isLoading, error } = useTask(Number.isInteger(taskId) ? taskId : null);
  const isOdcTask = String(task?.taskType || '').trim().toLowerCase() === 'odc';

  const serverLinkedTimecampTaskId = useMemo(() => {
    const raw = task?.timecampTaskId;
    if (raw === null || raw === undefined || raw === '') return null;
    const id = Number(raw);
    return Number.isInteger(id) && id > 0 ? id : null;
  }, [task?.timecampTaskId]);

  const serverLinkedTimecampTaskName = useMemo(() => {
    const s = task?.timecampTaskName;
    const name = typeof s === 'string' ? s.trim() : '';
    return name ? name : null;
  }, [task?.timecampTaskName]);

  const isTaskLinkWarningIgnored = task?.ignoreTimecampLinkWarning === true;

  useEffect(() => {
    setLinkedTimecampTaskId(serverLinkedTimecampTaskId);
    setTimecampLastSavedTaskId(serverLinkedTimecampTaskId);
    if (!Number.isInteger(serverLinkedTimecampTaskId)) {
      setLinkedTimecampTaskName(null);
    } else {
      setLinkedTimecampTaskName(serverLinkedTimecampTaskName);
    }
  }, [serverLinkedTimecampTaskId, serverLinkedTimecampTaskName]);

  const updateTask = useUpdateTask({
    onSuccess: (_data, variables) => {
      const payload = variables?.payload || {};
      const updatedMapping = payload?.timecampTaskID !== undefined || payload?.timecampTaskId !== undefined || payload?.timecamp_task_id !== undefined;
      const updatedIgnore = payload?.ignoreTimecampLinkWarning !== undefined || payload?.ignore_timecamp_link_warning !== undefined;

      toaster.push(
        <Message showIcon type="success" closable>
          {updatedMapping
            ? 'TimeCamp task mapping saved.'
            : updatedIgnore
              ? 'Task-link warning setting updated.'
              : 'Task updated.'}
        </Message>,
        { placement: 'topEnd', duration: 2400 }
      );
    },
  });

  const timecampTasksUrl = useMemo(() => {
    const apiBase = resolveApiBaseUrl();
    return `${apiBase.replace(/\/+$/, '')}/providers/timecamp/tasks?minimal=true`;
  }, []);

  const handleFetchTimecampTasks = useCallback(async () => {
    setTimecampLoading(true);
    setTimecampError(null);
    setTimecampTasksTree([]);
    setTimecampTreeIndex({ nodesById: new Map(), parentById: new Map() });
    try {
      const resp = await authenticatedFetch(timecampTasksUrl, { method: 'GET' });

      const text = await resp.text();
      const parsed = tryParseJsonText(text);

      if (parsed && typeof parsed === 'object') {
        const timecampPayload =
          parsed?.json && typeof parsed.json === 'object' ? parsed.json : parsed;
        const { tasks, error: jsonErr } = parseTimecampTasksJson(timecampPayload);
        if (jsonErr) {
          setTimecampError(jsonErr);
          return;
        }
        const tree = buildTimecampTaskTree(tasks);
        setTimecampTasksTree(tree.roots);
        setTimecampTreeIndex({ nodesById: tree.nodesById, parentById: tree.parentById });
        return;
      }

      // Fallback: raw response (likely HTML error page)
      if (isProbablyHtml(text)) {
        setTimecampError(
          `Received HTML instead of JSON. This usually means the API base URL is wrong (proxy not hit) or you got an auth/redirect page. URL: ${timecampTasksUrl}`
        );
        return;
      }

      const direct = tryParseJsonText(text);
      if (direct && typeof direct === 'object') {
        const { tasks, error: jsonErr } = parseTimecampTasksJson(direct);
        if (jsonErr) {
          setTimecampError(jsonErr);
          return;
        }
        const tree = buildTimecampTaskTree(tasks);
        setTimecampTasksTree(tree.roots);
        setTimecampTreeIndex({ nodesById: tree.nodesById, parentById: tree.parentById });
        return;
      }

      setTimecampError('Unexpected response format (not JSON or HTML).');
    } catch (e) {
      setTimecampError(e?.message || String(e));
    } finally {
      setTimecampLoading(false);
    }
  }, [authenticatedFetch, timecampTasksUrl]);

  useEffect(() => {
    if (!apiModalOpen) return;
    // Auto-load tasks when opening the modal.
    handleFetchTimecampTasks();
  }, [apiModalOpen, handleFetchTimecampTasks]);

  useEffect(() => {
    if (!apiModalOpen) return;
    if (timecampSelectedTaskId !== null && timecampSelectedTaskId !== undefined) return;
    if (!Number.isInteger(linkedTimecampTaskId)) return;
    setTimecampSelectedTaskId(linkedTimecampTaskId);
  }, [apiModalOpen, linkedTimecampTaskId, timecampSelectedTaskId]);

  const selectedTimecampNode = useMemo(() => {
    const id = Number(timecampSelectedTaskId);
    if (!Number.isInteger(id)) return null;
    return timecampTreeIndex?.nodesById?.get?.(id) || null;
  }, [timecampSelectedTaskId, timecampTreeIndex]);

  const selectedTimecampPath = useMemo(() => {
    if (!Number.isInteger(Number(timecampSelectedTaskId))) return '';
    const labels = buildTimecampPathLabels(
      timecampSelectedTaskId,
      timecampTreeIndex.nodesById,
      timecampTreeIndex.parentById
    );
    return labels.join(' / ');
  }, [timecampSelectedTaskId, timecampTreeIndex]);

  const canSaveTimecampSelection = useMemo(() => {
    if (!apiModalOpen) return false;
    if (!Number.isInteger(taskId)) return false;
    const id = Number(timecampSelectedTaskId);
    if (!Number.isInteger(id)) return false;
    if (Number(timecampLastSavedTaskId) === id) return false;
    return true;
  }, [apiModalOpen, taskId, timecampLastSavedTaskId, timecampSelectedTaskId]);

  const saveTimecampSelection = async (id) => {
    if (!apiModalOpen) return;
    if (!Number.isInteger(taskId)) return;
    if (!Number.isInteger(id)) return;

    if (Number(timecampLastSavedTaskId) === Number(id)) return;
    if (Number(timecampLastRequestedTaskIdRef.current) === Number(id)) return;
    if (timecampSaveInFlightRef.current) return;

    timecampLastRequestedTaskIdRef.current = id;
    timecampSaveInFlightRef.current = true;
    setTimecampSaveError(null);
    setTimecampSaveLoading(true);

    try {
      await updateTask.mutateAsync({
        taskId,
        projectId: Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : undefined,
        payload: {
          timecampTaskID: id,
          timecampTaskName: selectedTimecampNode?.label ?? null,
          ignoreTimecampLinkWarning: false,
        },
      });
      setTimecampLastSavedTaskId(id);
      setLinkedTimecampTaskId(id);
      if (selectedTimecampNode?.label) {
        setLinkedTimecampTaskName(String(selectedTimecampNode.label));
      } else {
        setLinkedTimecampTaskName(null);
      }
    } catch (e) {
      setTimecampSaveError(e?.message || String(e));
    } finally {
      timecampSaveInFlightRef.current = false;
      setTimecampSaveLoading(false);
    }
  };

  const projectId = Number.isInteger(projectIdParam)
    ? projectIdParam
    : (Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : null);
  const taskOrderId = Number.isInteger(taskOrderIdParam)
    ? taskOrderIdParam
    : (Number.isInteger(Number(task?.taskOrderId)) ? Number(task.taskOrderId) : null);

  const { data: taskOrderTasksRes = [] } = useTasks(
    { projectId, taskOrderId },
    { enabled: Number.isInteger(projectId) && Number.isInteger(taskOrderId) }
  );

  const taskOrderTaskPickerData = useMemo(() => {
    const rows = Array.isArray(taskOrderTasksRes) ? taskOrderTasksRes : [];
    const taskRows = rows
      .map((t) => {
        const id = Number(t?.id);
        if (!Number.isInteger(id)) return null;
        const label = String(t?.task || '').trim() || `Task ${id}`;
        return { value: id, label };
      })
      .filter((x) => !!x)
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));

    if (Number.isInteger(projectId) && Number.isInteger(taskOrderId)) {
      return [{ value: '__full_task_order__', label: 'Full Task Order' }, ...taskRows];
    }

    return taskRows;
  }, [projectId, taskOrderId, taskOrderTasksRes]);

  const showTaskOrderTaskSwitcher = Number.isInteger(projectId) && Number.isInteger(taskOrderId) && taskOrderTaskPickerData.length > 0;

  const taskOrderPath =
    Number.isInteger(projectId) && Number.isInteger(taskOrderId)
      ? `/projects/${projectId}/task-orders/${taskOrderId}`
      : null;

  const firstActivityId = Number(Array.isArray(task?.activities) ? task.activities?.[0]?.id : null);
  const taskForecastPath = Number.isInteger(taskId) && Number.isInteger(firstActivityId)
    ? (Number.isInteger(projectId) && Number.isInteger(taskOrderId)
      ? `/projects/${projectId}/task-orders/${taskOrderId}/tasks/${taskId}/activities/${firstActivityId}/forecast`
      : `/tasks/${taskId}/activities/${firstActivityId}/forecast`)
    : null;

  const actualsReturnPath = `${location?.pathname || ''}${location?.search || ''}`;

  return (
    <div style={{ width: '90%', maxWidth: 1600, margin: '0 auto' }}>
      <Stack justifyContent="space-between" alignItems="center" wrap>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, lineHeight: '22px' }}>Actuals</h3>
          </div>
          <div
            style={{
              color: '#666',
              fontSize: 13,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task?.task ? `Task: ${task.task}` : isLoading ? 'Loading task…' : '—'}
          </div>
        </div>

        <Stack spacing={10} wrap>
          {showTaskOrderTaskSwitcher ? (
            <div style={{ minWidth: 320, display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 13, color: '#666', fontWeight: 700 }}>Task</div>
              <div style={{ minWidth: 260, flex: 1 }}>
                <SelectPicker
                  block
                  searchable
                  cleanable={false}
                  size="sm"
                  placeholder="Switch task"
                  data={taskOrderTaskPickerData}
                  value={Number.isInteger(taskId) ? taskId : null}
                  onChange={(value) => {
                    if (value === '__full_task_order__') {
                      if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) return;
                      const search = location?.search || '';
                      navigate(`/projects/${projectId}/task-orders/${taskOrderId}/actuals${search}`, {
                        state: {
                          fromTaskActuals: true,
                          taskActualsPath: actualsReturnPath || null,
                        },
                      });
                      return;
                    }

                    const nextTaskId = Number(value);
                    if (!Number.isInteger(nextTaskId)) return;
                    if (!Number.isInteger(projectId) || !Number.isInteger(taskOrderId)) return;
                    const search = location?.search || '';
                    navigate(`/projects/${projectId}/task-orders/${taskOrderId}/tasks/${nextTaskId}/actuals${search}`);
                  }}
                />
              </div>
            </div>
          ) : null}
          <Button
            appearance="ghost"
            onClick={() => {
              if (taskOrderPath) {
                navigate(taskOrderPath);
                return;
              }
              if (Number.isInteger(taskId)) navigate(`/tasks/${taskId}`);
              else navigate('/');
            }}
          >
            Back
          </Button>
        </Stack>
      </Stack>

      <Divider />

      {showProviderConfigCta ? (
        <div style={{ marginBottom: 12 }}>
          <Message type="warning" showIcon closable>
            You have not configured the source for your actual hours.{' '}
            <Button appearance="link" size="xs" onClick={() => navigate('/configuration')}>
              Click here to configure
            </Button>
          </Message>
        </div>
      ) : null}

      {isTimeCampActive ? (
        Number.isInteger(linkedTimecampTaskId) && linkedTimecampTaskId > 0 ? (
          <div style={{ marginBottom: 12 }}>
            <Message type="success" showIcon>
              <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>TimeCamp task successfully linked</div>
                  <div style={{ fontSize: 13, color: '#2e2e2e', marginTop: 4, minWidth: 0 }}>
                    {linkedTimecampTaskName ? (
                      <span style={{ fontWeight: 700 }}>{linkedTimecampTaskName}</span>
                    ) : (
                      <span style={{ fontWeight: 700 }}>ID: {linkedTimecampTaskId}</span>
                    )}
                  </div>
                </div>
                <Stack spacing={8}>
                  {isTaskLinkWarningIgnored ? (
                    <Button
                      appearance="subtle"
                      size="sm"
                      loading={updateTask.isPending}
                      onClick={async () => {
                        await updateTask.mutateAsync({
                          taskId,
                          projectId: Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : undefined,
                          payload: { ignoreTimecampLinkWarning: false },
                        });
                      }}
                    >
                      Stop ignoring
                    </Button>
                  ) : null}
                  <Button appearance="ghost" size="sm" onClick={() => setApiModalOpen(true)}>
                    Edit
                  </Button>
                </Stack>
              </Stack>
            </Message>
          </div>
        ) : (
          <div style={{ marginBottom: 12 }}>
            <Message type="warning" showIcon>
              <Stack justifyContent="space-between" alignItems="center" wrap spacing={10}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>No TimeCamp task linked for this task</div>
                  {isOdcTask ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                      Optional for ODC tasks.
                    </div>
                  ) : null}
                  {isTaskLinkWarningIgnored ? (
                    <div style={{ marginTop: 4, fontSize: 12, color: '#0958d9', fontWeight: 700 }}>
                      Link warning is currently ignored for this task.
                    </div>
                  ) : null}
                </div>
                <Stack spacing={8}>
                  {isTaskLinkWarningIgnored ? (
                    <Button
                      appearance="subtle"
                      size="sm"
                      loading={updateTask.isPending}
                      onClick={async () => {
                        await updateTask.mutateAsync({
                          taskId,
                          projectId: Number.isInteger(Number(task?.projectId)) ? Number(task.projectId) : undefined,
                          payload: { ignoreTimecampLinkWarning: false },
                        });
                      }}
                    >
                      Stop ignoring
                    </Button>
                  ) : null}
                  <Button appearance="primary" size="sm" onClick={() => setApiModalOpen(true)}>
                    Select Task
                  </Button>
                </Stack>
              </Stack>
            </Message>
          </div>
        )
      ) : null}

      {error ? <Message type="error" showIcon>Failed to load task.</Message> : null}
      {!Number.isInteger(taskId) ? <Message type="error" showIcon>Invalid task id.</Message> : null}

      <Panel
        bordered
      >

        {isLoading ? <div>Loading task…</div> : null}
        {!isLoading ? (
          <TaskPeopleMonthlyLayout
            taskId={taskId}
            task={task}
            timecampTaskId={linkedTimecampTaskId}
            activeProviderName={activeProviderName}
            provider={activeProvider}
            providerLoading={providerLoading}
            autoPullMonthStart={autoPullMonthStart}
            taskForecastPath={taskForecastPath}
            actualsReturnPath={actualsReturnPath}
            onOpenTaskForecast={(path, state) => navigate(path, { state })}
          />
        ) : null}
      </Panel>

      {isTimeCampActive ? (
        <Modal open={apiModalOpen} onClose={() => setApiModalOpen(false)} size="lg" overflow>
          <Modal.Header>
            <Modal.Title>Link TimeCamp task</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
              Choose the equivalent <b>TimeCamp task</b> that hours for <b>{task?.task ? task.task : 'this ProPlan task'}</b> will be logged under.
            </div>

            <Stack justifyContent="space-between" alignItems="center" wrap style={{ marginBottom: 12 }} spacing={10}>
              <div style={{ color: '#666', fontSize: 12 }}>
                Source: provider endpoint <b>/api/providers/timecamp/tasks</b> (configured via <b>/api/providers/active</b>)
              </div>
              <Button
                appearance="subtle"
                size="xs"
                onClick={handleFetchTimecampTasks}
                loading={timecampLoading}
                title="Refresh"
                aria-label="Refresh"
              >
                <i className="bi bi-arrow-clockwise" />
              </Button>
            </Stack>

            {timecampError ? (
              <Message type="error" showIcon style={{ marginBottom: 12 }}>
                {timecampError}
              </Message>
            ) : null}

            {timecampSaveError ? (
              <Message type="error" showIcon style={{ marginBottom: 12 }}>
                Failed to save mapping: {timecampSaveError}
              </Message>
            ) : null}

            {Array.isArray(timecampTasksTree) && timecampTasksTree.length > 0 ? (
              <div style={{ display: 'grid', gap: 12, marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>Browse TimeCamp tasks (multi-level)</div>
                  <Cascader
                    searchable
                    block
                    parentSelectable
                    placeholder="Select the TimeCamp task…"
                    data={timecampTasksTree}
                    columnWidth={360}
                    popupStyle={{ minWidth: 920 }}
                    value={Number.isInteger(Number(timecampSelectedTaskId)) ? Number(timecampSelectedTaskId) : null}
                    onChange={(v) => {
                      const nextId = Number.isInteger(Number(v)) ? Number(v) : null;
                      if (Number(timecampSelectedTaskId) === Number(nextId)) return;
                      setTimecampSelectedTaskId(nextId);
                    }}
                    renderMenuItem={(label, item) => (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                        <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>#{item?.value}</span>
                      </span>
                    )}
                  />
                </div>

                <Panel bordered>
                  <div style={{ fontSize: 12, color: '#666', fontWeight: 700, marginBottom: 6 }}>Selected project/task</div>
                  {selectedTimecampNode ? (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 14, lineHeight: '18px' }}>{selectedTimecampNode.label}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>ID: {selectedTimecampNode.value}</div>
                      </div>

                      {selectedTimecampPath ? (
                        <div style={{ fontSize: 12, color: '#444' }}>
                          <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>Path</div>
                          <div style={{ fontWeight: 700, wordBreak: 'break-word' }}>{selectedTimecampPath}</div>
                        </div>
                      ) : null}

                      {Number(timecampLastSavedTaskId) === Number(selectedTimecampNode.value) ? (
                        <div style={{ fontSize: 12, color: '#0a6b6c', fontWeight: 700 }}>Saved to task.</div>
                      ) : null}
                    </div>
                  ) : (
                    <div style={{ color: '#666', fontSize: 12 }}>Pick a TimeCamp item to map to this task.</div>
                  )}
                </Panel>
              </div>
            ) : null}
          </Modal.Body>
          <Modal.Footer>
            <Button
              appearance="primary"
              loading={timecampSaveLoading}
              disabled={!canSaveTimecampSelection || timecampLoading}
              onClick={async () => {
                const id = Number(timecampSelectedTaskId);
                if (!Number.isInteger(id)) return;
                await saveTimecampSelection(id);
              }}
            >
              Save
            </Button>
            <Button onClick={() => setApiModalOpen(false)} appearance="ghost" disabled={timecampSaveLoading}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
}
